import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ServicesMarquee } from '../components/ServicesMarquee';
import { LoginPortals } from '../components/LoginPortals';
import { ComplianceCalendar } from '../components/ComplianceCalendar';
import { FAQ } from '../components/FAQ';
import { QuickEnquiryForm } from '../components/QuickEnquiryForm';
import { HowWeHelp } from '../components/HowWeHelp';
import { ServicesExplorer } from '../components/ServicesExplorer';
import { FeaturedServices } from '../components/FeaturedServices';
import { WhyChooseUs } from '../components/WhyChooseUs';
import { AnimatedStat } from '../components/AnimatedStat';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, Sparkles, ArrowRight, Phone, Star } from 'lucide-react';
import useSEO from '../hooks/useSEO';

const HomePage = () => {
  const [isQuickEnquiryOpen, setIsQuickEnquiryOpen] = useState(false);

  useSEO({
    title: 'Utkarshudyam — India\'s Trusted Business & Compliance Partner',
    description: 'Business registration, GST, MSME, Startup India, ISO, FSSAI, trademarks, licenses and business loans — end-to-end professional support for Indian startups and MSMEs.',
    canonical: typeof window !== 'undefined' ? window.location.origin + '/' : undefined,
    image: '/company-logo.png',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Utkarshudyam Solution Private Limited',
      url: typeof window !== 'undefined' ? window.location.origin : 'https://utkarshudyam.com',
      logo: (typeof window !== 'undefined' ? window.location.origin : '') + '/company-logo.png',
      email: 'Sales@utkarshudyam.com',
      telephone: '+91-9755085383',
      sameAs: [],
      address: { '@type': 'PostalAddress', addressCountry: 'IN' },
    },
  });

  const testimonials = [
    { name: 'JANKI SAKHI INDIA PVT LTD', rating: 5, text: 'Highly recommended for company registration and compliance services. Professional team with excellent domain knowledge.' },
    { name: 'METARIG ENGINEERING LLP', rating: 5, text: 'Very fast delivery with transparent pricing. They handled our ROC filings without a single query from our side.' },
    { name: 'Rajesh Kumar, Founder', rating: 5, text: 'Excellent service for GST registration and startup certification. Guided us through every step.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Running Services Marquee (right below fixed header) */}
      <div className="pt-[116px]">
        <ServicesMarquee />
      </div>

      {/* Hero */}
      <section className="relative pt-14 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A2463] via-[#0d2d6e] to-[#247BA0] opacity-95" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#FFB703] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#247BA0] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{ animationDelay: '1s' }} />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-[#FFB703] hover:bg-[#FB8500] text-white px-6 py-2 text-sm font-semibold">
              <Sparkles className="inline mr-2" size={16} /> Trusted by 500+ Indian Businesses
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              India's Trusted<br />
              <span className="bg-gradient-to-r from-[#FFB703] to-[#FB8500] bg-clip-text text-transparent">Business & Compliance Partner</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mb-10 leading-relaxed max-w-3xl mx-auto">
              Empowering Startups, MSMEs and Growing Businesses with End-to-End Registration, Compliance, Certification and Business Growth Solutions.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Link to="/contact">
                <Button size="lg" data-testid="hero-consult-btn" className="bg-gradient-to-r from-[#FFB703] to-[#FB8500] hover:from-[#FB8500] hover:to-[#FFB703] text-white text-base md:text-lg px-8 py-6 shadow-2xl transition-all hover:scale-105">
                  Get Free Consultation <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" data-testid="hero-explore-btn" className="border-2 border-white text-white hover:bg-white hover:text-[#0A2463] text-base md:text-lg px-8 py-6 transition-all">
                  Explore Services
                </Button>
              </Link>
              <a href="https://wa.me/919316085383" target="_blank" rel="noreferrer">
                <Button size="lg" data-testid="hero-whatsapp-btn" className="bg-[#25d366] hover:bg-[#128c7e] text-white text-base md:text-lg px-8 py-6 transition-all hover:scale-105">
                  <Phone className="mr-2" size={18} /> WhatsApp Now
                </Button>
              </a>
            </div>

            <div className="mt-10 flex flex-wrap justify-center gap-x-6 gap-y-2 text-white/90 text-sm">
              <div className="flex items-center gap-1.5"><CheckCircle2 size={16} className="text-[#FFB703]" /> DPIIT Certified</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 size={16} className="text-[#FFB703]" /> ISO Registered</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 size={16} className="text-[#FFB703]" /> 500+ Happy Clients</div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats card floating over hero end */}
      <section className="py-12 bg-white relative -mt-14 z-20">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <AnimatedStat value={500} suffix="+" label="Happy Clients" />
              <AnimatedStat value={1000} suffix="+" label="Services Delivered" />
              <AnimatedStat value={50} suffix="+" label="Types of Services" />
              <AnimatedStat value={100} suffix="%" label="Client Focus" />
            </div>
          </div>
        </div>
      </section>

      {/* How We Help (dark navy 3-card) */}
      <HowWeHelp />

      {/* Services Explorer — 6 category tabs */}
      <ServicesExplorer />

      {/* Popular / Featured Services */}
      <FeaturedServices />

      {/* Why choose us */}
      <WhyChooseUs />

      {/* Compliance Calendar */}
      <ComplianceCalendar />

      {/* Testimonials */}
      <section className="py-20 bg-white" data-testid="testimonials-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block bg-[#0A2463]/5 text-[#0A2463] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">Client Reviews</span>
            <h2 className="text-3xl md:text-4xl font-black text-[#0A2463] mt-3">Trusted by Growing Businesses Across India</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(t.rating)].map((_, i) => <Star key={i} size={16} className="fill-[#FFB703] text-[#FFB703]" />)}
                </div>
                <p className="text-gray-700 leading-relaxed italic">"{t.text}"</p>
                <div className="mt-4 pt-4 border-t border-gray-100 text-sm font-bold text-[#0A2463]">{t.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Login Portals (Admin / Mobile CRM / ERP) */}
      <LoginPortals />

      {/* FAQ */}
      <FAQ />

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0A2463] via-[#0d2d6e] to-[#247BA0] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 right-10 w-96 h-96 bg-[#FFB703] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-black text-white">Ready to Start or Scale Your Business?</h2>
            <p className="text-lg md:text-xl text-gray-200 mt-4">Talk to our experts today — compliance made simple.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-gradient-to-r from-[#FFB703] to-[#FB8500] text-white text-lg px-10 py-6 shadow-2xl transition-all hover:scale-105">Apply Now</Button>
              </Link>
              <a href="tel:9316085383">
                <Button size="lg" className="bg-white text-[#0A2463] hover:bg-gray-100 text-lg px-10 py-6 shadow-xl transition-all hover:scale-105">
                  <Phone className="mr-2" size={18} /> Call: 9316085383
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <QuickEnquiryForm isOpen={isQuickEnquiryOpen} onClose={() => setIsQuickEnquiryOpen(false)} preSelectedService="" />
    </div>
  );
};

export default HomePage;
